import React, { useState, useEffect, useCallback } from 'react';
import { Box, CircularProgress, IconButton, Chip, Typography, Stack, Divider, Card, CardContent } from '@mui/material';
import {
  MenuBook as LessonPlanIcon,
  Refresh as RefreshIcon,
  CalendarToday as CalendarIcon,
  School as SchoolIcon,
  Subject as SubjectIcon,
  Download as DownloadIcon,
  Person as PersonIcon,
  AttachFile as AttachFileIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api, { handleCatch } from '../api';
import colors from '../common/Colors';
import MobileHeader from '../components/MobileHeader';
import EmptyState from '../components/EmptyState';
import moment from 'moment';

const LessonPlanScreen = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const loadData = async (page = 1) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('userToken');
      const response = await api.get(
        `api/school/lesson-plan/student?PageSize=10&PageNumber=${page}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      const data = response.data.data; 

      if (page === 1) {
        setItems(data);
      } else {
        setItems((prevItems) => [...prevItems, ...data]);
      }

      setHasMore(data.length === 10);
      setPageNumber(page + 1);
    } catch (error) {
      handleCatch(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData(1);
  }, []);

  const onRefresh = useCallback(async () => {
    setHasMore(true);
    setPageNumber(1);
    await loadData(1);
  }, []);

  const onView = (item) => {
    navigate('/lesson-plan-details', { state: { item } });
  };

  const handleDownload = (item, e) => {
    e.stopPropagation();
    if (item.fileUrl) {
      window.open(item.fileUrl, '_blank');
    }
  };

  const loadMore = () => {
    if (!loading && hasMore) {
      loadData(pageNumber);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return moment(dateString).format('DD MMM YYYY');
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <MobileHeader
        title="Lesson Plans"
        showBack={true}
        rightAction={
          <IconButton onClick={onRefresh} disabled={loading} sx={{ color: '#fff' }}>
            <RefreshIcon />
          </IconButton>
        }
      />

      <Box sx={{ flex: 1, overflow: 'auto', bgcolor: '#f5f5f5' }}>
        {loading && items.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <CircularProgress />
          </Box>
        ) : items.length === 0 ? (
          <EmptyState
            icon={<LessonPlanIcon sx={{ fontSize: 64, color: colors.GRAY }} />}
            title="No Lesson Plans"
            message="No published lesson plans available for your class. Tap the refresh button above to check for updates."
          />
        ) : (
          <>
            {items.map((item) => (
              <Card
                key={item.id}
                onClick={() => onView(item)}
                sx={{
                  mx: 2,
                  mb: 2,
                  borderLeft: `4px solid ${colors.PRIMARY}`,
                  cursor: 'pointer',
                  '&:hover': {
                    boxShadow: 3,
                  },
                  '&:active': {
                    bgcolor: '#f5f5f5',
                  },
                }}
              >
                <CardContent>
                  <Stack spacing={1.5}>
                  {/* Topic */}
                  <Typography variant="h6" fontWeight="600" color="primary">
                    {item.topic}
                  </Typography>

                  {/* Subject */}
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <SubjectIcon sx={{ fontSize: 18, color: colors.PRIMARY }} />
                    <Typography variant="body1" fontWeight="500">
                      {item.subjectName}
                    </Typography>
                  </Stack>

                  <Divider />

                  {/* Posted By and Date */}
                  <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flex: 1 }}>
                      <PersonIcon sx={{ fontSize: 16, color: colors.GRAY }} />
                      <Typography variant="body2" color="text.secondary">
                        {item.createdBy}
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <CalendarIcon sx={{ fontSize: 16, color: colors.GRAY }} />
                      <Typography variant="body2" color="text.secondary">
                        {formatDate(item.creationTime)}
                      </Typography>
                    </Stack>
                  </Stack>

                  {/* Date Range */}
                  <Stack 
                    direction="row" 
                    spacing={1} 
                    alignItems="center" 
                    sx={{ 
                      bgcolor: '#f5f5f5', 
                      p: 1, 
                      borderRadius: 1 
                    }}
                  >
                    <Typography variant="caption" color="text.secondary" fontWeight="500">
                      Duration:
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatDate(item.startDate)} to {formatDate(item.endDate)}
                    </Typography>
                  </Stack>

                  {/* Attachment Indicator */}
                  {item.fileName && (
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <AttachFileIcon sx={{ fontSize: 16, color: colors.PRIMARY }} />
                      <Typography variant="caption" color="primary">
                        Attachment available
                      </Typography>
                    </Stack>
                  )}
                </Stack>
              </CardContent>
            </Card>
            ))}

            {/* Load More */}
            {hasMore && (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                {loading ? (
                  <CircularProgress size={24} />
                ) : (
                  <Typography
                    variant="body2"
                    color="primary"
                    sx={{ cursor: 'pointer' }}
                    onClick={loadMore}
                  >
                    Load More
                  </Typography>
                )}
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default LessonPlanScreen;
